'use client';

import { useEffect } from 'react';
import { initReactI18next, useTranslation as originalHook } from 'react-i18next';

import dayjs from 'dayjs';
import i18next, { CustomTypeOptions, InitOptions } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';

import { optionsBase } from './config';
import { languageCodes } from './languages';

// this expression has been put here because this file is loaded nearly instantly
dayjs.locale('en', { weekStart: 1 });

const runsOnServerSide = typeof window === 'undefined';

const optionsClient = (): InitOptions => ({
    ...optionsBase,
    lng: undefined, // let detect the language on client side
    detection: { order: ['path', 'htmlTag'] },
    preload: runsOnServerSide ? languageCodes : [],
});

i18next
    .use(initReactI18next)
    .use(LanguageDetector)
    .use(
        resourcesToBackend(
            (lang: string, namespace: string) => import(`../../public/locales/${lang}/${namespace}.json`),
        ),
    )
    .init(optionsClient());

export function useTranslation<Ns extends CustomTypeOptions['ns'] = CustomTypeOptions['ns']>(
    lang: string,
    namespace: Ns,
) {
    const useT = originalHook(namespace);
    const { i18n } = useT;
    if (runsOnServerSide && i18n.resolvedLanguage !== lang) {
        i18n.changeLanguage(lang);
    } else {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            if (i18n.resolvedLanguage === lang) return;
            i18n.changeLanguage(lang);
        }, [lang, i18n]);
    }
    return useT as ReturnType<typeof originalHook<typeof namespace>>;
}
